// The Code workspace's Output panel: every line a run produced, newest at
// the bottom - `print` output, the script's result, each simulated engine
// effect, and any diagnostic that stopped it, each stamped with the time
// and filterable by kind (Godot's Output panel has the same four-way
// filter; Roblox's Output window the same click-to-jump on an error).
import { useEffect, useMemo, useRef } from 'react';

import { revealInScript } from '../scripting/editorCommands';
import { entryMatchesFilter, useCodeRunStore, type OutputEntry, type OutputFilter, type OutputKind } from '../scripting/codeRunStore';
import { CodeIcon, type CodeIconKind } from './codeIcons';
import { CwToolButton, formatClock, MOD_KEY } from './codeShared';

const FILTERS: readonly { id: OutputFilter; label: string }[] = [
  { id: 'all', label: 'All' },
  { id: 'print', label: 'Prints' },
  { id: 'effect', label: 'Effects' },
  { id: 'problem', label: 'Problems' },
];

const ICON_FOR_KIND: Record<OutputKind, CodeIconKind | null> = {
  run: 'play',
  print: null,
  effect: 'bolt',
  result: 'arrow',
  error: 'error',
  warning: 'warning',
  sys: null,
};

function OutputRow({ entry }: { entry: OutputEntry }) {
  const canJump = entry.scriptId !== undefined && entry.line !== undefined;
  const icon = ICON_FOR_KIND[entry.kind];
  return (
    <div
      className={`cw-out cw-out--${entry.kind}${canJump ? ' is-link' : ''}`}
      onClick={canJump ? () => revealInScript(entry.scriptId!, entry.line!, entry.column ?? 1) : undefined}
      role={canJump ? 'button' : undefined}
      tabIndex={canJump ? 0 : undefined}
      onKeyDown={canJump ? (e) => e.key === 'Enter' && revealInScript(entry.scriptId!, entry.line!, entry.column ?? 1) : undefined}
      title={canJump ? `Go to ${entry.scriptName}.hws, line ${entry.line}` : undefined}
    >
      <span className="cw-out-time">{formatClock(entry.time)}</span>
      <span className="cw-out-icon">{icon ? <CodeIcon kind={icon} size={12} /> : entry.kind === 'print' ? <span style={{ opacity: 0.5 }}>›</span> : null}</span>
      <span className="cw-out-text">
        {entry.area && <span className="cw-out-area">{entry.area}</span>}
        {entry.text}
        {canJump && (
          <span className="cw-out-loc">
            {entry.scriptName}.hws:{entry.line}
            {entry.column !== undefined ? `:${entry.column}` : ''}
          </span>
        )}
      </span>
    </div>
  );
}

export function CodeOutputPanel() {
  const entries = useCodeRunStore((s) => s.entries);
  const filter = useCodeRunStore((s) => s.filter);
  const search = useCodeRunStore((s) => s.search);
  const clearOnRun = useCodeRunStore((s) => s.clearOnRun);
  const running = useCodeRunStore((s) => s.running);
  const setFilter = useCodeRunStore((s) => s.setFilter);
  const setSearch = useCodeRunStore((s) => s.setSearch);
  const setClearOnRun = useCodeRunStore((s) => s.setClearOnRun);
  const clear = useCodeRunStore((s) => s.clear);
  const stop = useCodeRunStore((s) => s.stop);
  const feedRef = useRef<HTMLDivElement>(null);

  const visible = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return entries.filter((e) => entryMatchesFilter(e, filter) && (!needle || e.text.toLowerCase().includes(needle)));
  }, [entries, filter, search]);

  // Follow the tail, like every console does, but only while the reader is
  // already at (or near) the bottom - scrolling up to read an earlier run
  // must not get yanked back down by a new line.
  const stickToBottom = useRef(true);
  useEffect(() => {
    const el = feedRef.current;
    if (el && stickToBottom.current) el.scrollTop = el.scrollHeight;
  }, [visible]);

  return (
    <div className="cw-fill">
      <div className="cw-toolbar" style={{ gap: 6 }}>
        <div role="tablist" aria-label="Output filter" style={{ display: 'flex', gap: 2 }}>
          {FILTERS.map((f) => (
            <button key={f.id} type="button" role="tab" aria-selected={filter === f.id} className={'cw-tool' + (filter === f.id ? ' is-active' : '')} onClick={() => setFilter(f.id)} style={{ height: 26, padding: '0 8px' }}>
              {f.label}
            </button>
          ))}
        </div>
        <div className="cw-toolbar-spacer" />
        {running && <CwToolButton icon="stop" label="Stop" showLabel={false} danger onClick={stop} />}
        <CwToolButton icon="clear" label="Clear output" showLabel={false} onClick={clear} disabled={entries.length === 0} />
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px 4px' }}>
        <label className="cw-search" style={{ height: 26 }}>
          <CodeIcon kind="search" size={12} />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Filter output" aria-label="Filter output" spellCheck={false} />
          {search && (
            <button type="button" onClick={() => setSearch('')} aria-label="Clear output filter">
              <CodeIcon kind="close" size={11} />
            </button>
          )}
        </label>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 'var(--ss-fs-2xs)', color: 'var(--ss-text-3)', whiteSpace: 'nowrap' }} title="Start each run with an empty Output panel">
          <button role="switch" aria-checked={clearOnRun} className={'ss-switch' + (clearOnRun ? ' is-on' : '')} onClick={() => setClearOnRun(!clearOnRun)} aria-label="Clear on run" style={{ transform: 'scale(0.8)', margin: '-2px -4px' }} />
          Clear on run
        </label>
      </div>
      <div
        ref={feedRef}
        className="cw-feed"
        role="log"
        aria-live="polite"
        onScroll={(e) => {
          const el = e.currentTarget;
          stickToBottom.current = el.scrollHeight - el.scrollTop - el.clientHeight < 24;
        }}
      >
        {entries.length === 0 && (
          <div className="cw-hint" style={{ padding: '10px 14px' }}>
            Run a script with the play button or {MOD_KEY}+Enter to see its output here. From the Code workspace a script runs in a
            sandbox: <code style={{ fontFamily: 'var(--ss-mono)' }}>print</code> lines, the returned value and every effect it would have on
            the scene are listed, without touching your project. Open Preview to run scripts against the live scene.
          </div>
        )}
        {entries.length > 0 && visible.length === 0 && <div className="cw-hint" style={{ padding: '10px 14px' }}>Nothing in this run matches the current filter.</div>}
        {visible.map((entry) => <OutputRow key={entry.id} entry={entry} />)}
      </div>
    </div>
  );
}
